import { JSX, use, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  dateCompare,
  dateFromString,
  dateRangesOverlap,
  DateString,
  daysBetween,
  getDateAsShortString,
  lastSundayDate,
  thisSundayDate,
} from '../../../common/src/date_string.ts';
import { inSafari } from '../../../common/src/environment.ts';
import {
  CustomMusic,
  defaultPlaylistName,
  EmailType,
  EventSongList,
  Hymn,
  IntervalSongList,
  IntervalType,
  Org,
  OrgBase,
  OrgType,
  SchoolTerm,
  seasonalSongListName,
  SongList,
  SongListEntry,
  SongListSchemaEntry,
  SongListType,
} from '../../../common/src/model.ts';
import { getSongListStartDate, hasInterval, isBound } from '../../../common/src/model_util.ts';
import {
  getHymnOfTheMonthOrTermTitle,
  getSundayTitle,
  getWeeklyDateForOrganization,
  isWeeklySongListForDate,
  thisSundayTitle,
  todayTitle,
} from '../../../common/src/org.ts';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { getShortTime, getTimeFromDateTime } from '../../../common/src/time_string.ts';
import { ensureUnreachable, naturalStringCompare } from '../../../common/src/util.ts';
import ChevronDownIcon from '../assets/chevron-down.svg?react';
import { localStorageGet, LocalStorageKey, usabilityTooltipDisplayCount } from '../data/client_local_storage.ts';
import { getDemoSongLists } from '../data/demo_song_list.ts';
import { HymnalWithHymns } from '../data/hymnals.ts';
import { synchronizeWeeklySongLists, useCurrentOrg, useOrgEventsViewed } from '../data/use_current_org.tsx';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { useHousehold } from '../data/use_household.tsx';
import { HymnalsContext } from '../data/use_hymnals.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { alert } from '../util/confirm.tsx';
import { getHymnFromSongListEntry, getUrlForPage, orgHymnalSubPage, PrimaryOrgHymnalId } from '../util/path.ts';
import { getTermForDateRange, getWeeklyListName } from '../util/school.ts';
import '../util/shared.css';
import { CheckMark } from '../util/shared.tsx';
import { SpeechBubble, SpeechBubbleOrientation } from '../util/speech_bubble.tsx';
import { useCurrentDate } from '../util/use_current_date.tsx';
import { useEffectOnce } from '../util/use_effect_once.ts';
import { useOnline } from '../util/use_online.tsx';
import { HymnalsViewEntry } from '../views/hymnals_view.tsx';
import { HymnsList } from '../views/hymns_list.tsx';
import { Menu, MenuEntry } from './admin/org/shared.tsx';
import { AskYourMusicLeader, ChurchRequired } from './church_required.tsx';
import { shareWithMusicLeader } from './onboarding/common.tsx';
import { openProfileDrawer } from './profile_drawer/profile_drawer_state.ts';

const thisWeekHymnalSlug = 'THIS-WEEK';

export interface Props {
  isDemoPage?: boolean;
  showThisSundayOnly?: boolean;
  customHeader?: JSX.Element;
}

export function OrgPage({ isDemoPage, showThisSundayOnly, customHeader }: Props) {
  const { currentOrg, setCurrentOrg, householdOrgs } = useCurrentOrg();
  const { household } = useHousehold();
  const [songsForWeek] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const { customMusic } = useCustomMusic();
  const allHymnals = use(HymnalsContext);
  const [hymnals, setHymnals] = useState<HymnalWithHymns[] | undefined>();
  const navigate = useNavigate();
  const { isInternalUser, isOrgAdmin, isStaff } = useUserAttributes();
  const { setEventUpdatesViewed, mostRecentlyViewedOrgEvents } = useOrgEventsViewed();
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const today = useCurrentDate();

  // this is distinct from the initial demo page itself - it is showing the demo song lists inside the app
  const showDemoSongList = !currentOrg || currentOrg.notInterested;

  useEffect(() => {
    if (!isDemoPage) {
      void synchronizeWeeklySongLists();
    }
  }, [isDemoPage, currentOrg?.id]);

  useEffectOnce(() => {
    return () => {
      if (!showThisSundayOnly) {
        setEventUpdatesViewed();
      }
    };
  });

  useEffect(() => {
    if (allHymnals.length === 0) {
      return;
    }
    let songLists: Array<SongList>;
    if (isDemoPage) {
      // the onboarding demo preview shows only the "This Sunday" song list
      songLists = getDemoSongLists().filter(({ type }) => type === SongListType.WorshipService);
    } else if (showDemoSongList) {
      songLists = getDemoSongLists();
    } else if (showThisSundayOnly) {
      const date = getWeeklyDateForOrganization(currentOrg, today);
      songLists = songsForWeek?.filter((songList) => isWeeklySongListForDate(currentOrg, songList, date));
    } else {
      songLists = songsForWeek;
    }
    // Derived state written inside an effect — the rule would prefer useMemo, but the early-return
    // above (skip update when allHymnals is empty) means a useMemo refactor would flip behavior for
    // the empty→non-empty→empty transition (useMemo would return undefined, the current code
    // preserves the last computed value). Keeping the effect to avoid that subtle regression.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setHymnals(
      getHymnalsFromSongLists({
        songLists,
        org: currentOrg,
        allHymnals,
        customMusic,
        isDemo: isDemoPage,
        showThisSundayOnly,
        isInternalUser,
        isOrgAdmin,
        isStaff,
        mostRecentlyViewedOrgEvents,
        today,
      }),
    );
  }, [
    allHymnals,
    currentOrg,
    customMusic,
    isOrgAdmin,
    isDemoPage,
    isInternalUser,
    showDemoSongList,
    showThisSundayOnly,
    songsForWeek,
    mostRecentlyViewedOrgEvents,
    isStaff,
    today,
  ]);

  const contactYourLeadership = (
    <div className="inlineLink" onClick={shareWithMusicLeader}>
      Contact your leadership
    </div>
  );

  const onEditHymnal = useCallback(
    async (hymnal: HymnalWithHymns) => {
      if (hymnal.songListType === SongListType.Event) {
        await navigate(getUrlForPage(Pages.EventCalendar));
      } else {
        await navigate(getUrlForPage(Pages.Planner));
      }
    },
    [navigate],
  );

  let message: JSX.Element | undefined;
  if (isDemoPage) {
    message = undefined;
  } else if (!household) {
    // this code path should be impossible (RootPage should have navigated to onboarding demo, passing isDemo=true)
    // displaying URL (window.location.href) as a debugging tool because some customers are still seeing it... :(
    message = (
      <div>
        To view songs for your church, <AskYourMusicLeader /> to add your church to the app or{' '}
        <span className="inlineLink" onClick={openProfileDrawer}>
          sign in
        </span>{' '}
        if your church already participates.
        <br />
        <br />
        <p>{window.location.href}</p>
      </div>
    );
  } else if (!currentOrg) {
    message = <ChurchRequired />;
  } else if (currentOrg.notInterested) {
    message = (
      <div>
        Your church is not partnered with Sing Your Part. {contactYourLeadership} if you&apos;d like them to consider
        joining.
      </div>
    );
  } else if (songsForWeek === undefined) {
    message = <div>Downloading songs for this week...</div>;
  } else if (hymnals && hymnals.length === 0 && currentOrg.type === OrgType.Church) {
    message = (
      <div>
        <RequestSongs /> that your music leader upload this week’s songs.
      </div>
    );
  }

  const [isOrgChooserVisible, setIsOrgChooserVisible] = useState(false);
  const isOnline = useOnline();
  const onToggleOrgChooser = useCallback(() => {
    // nothing to switch to with a single org (the chevron affordance is hidden too)
    if (householdOrgs.length <= 1) {
      return;
    }
    if (!isOnline) {
      void alert({ confirmation: <span>An internet connection is required to switch organizations.</span> });
      return;
    }
    setIsOrgChooserVisible((isOrgChooserVisible) => !isOrgChooserVisible);
  }, [householdOrgs.length, isOnline]);
  const [tooltipCount, setTooltipCount] = useLocalStorage(LocalStorageKey.TooltipVisibilityCount);
  const titleText = currentOrg?.name ? `${currentOrg?.name}${showDemoSongList ? ' (Demo)' : ''}` : 'Your Church';
  const title = (
    <OuterTitleWrapper>
      <TitleWrapper onClick={onToggleOrgChooser} $clickable={householdOrgs.length > 1}>
        <InnerTitleWrapper>{titleText}</InnerTitleWrapper>
        {householdOrgs.length > 1 && <ChevronDown />}
      </TitleWrapper>
      {isOrgChooserVisible && (
        <Menu>
          {householdOrgs
            .sort((lhs, rhs) => naturalStringCompare(lhs.name, rhs.name))
            .map((org) => (
              <OrgMenuEntry
                org={org}
                key={org.id}
                householdOrgs={householdOrgs}
                currentOrg={currentOrg}
                setCurrentOrg={setCurrentOrg}
                onToggleOrgChooser={onToggleOrgChooser}
              />
            ))}
        </Menu>
      )}
      {householdOrgs.length > 1 &&
        !isPlayerMaximized &&
        (tooltipCount.switchOrganization ?? 0) <= usabilityTooltipDisplayCount && (
          <SpeechBubble $orientation={SpeechBubbleOrientation.Vertical}>
            <Tooltip>Tap to switch organizations</Tooltip>
          </SpeechBubble>
        )}
    </OuterTitleWrapper>
  );
  useEffect(() => {
    if (householdOrgs.length <= 1 || isPlayerMaximized) {
      return;
    }
    setTooltipCount((tooltipCount) =>
      (tooltipCount.switchOrganization ?? 0) < usabilityTooltipDisplayCount + 1
        ? { ...tooltipCount, switchOrganization: (tooltipCount.switchOrganization ?? 0) + 1 }
        : tooltipCount,
    );
  }, [householdOrgs.length, isPlayerMaximized, setTooltipCount]);

  const churchHymnal =
    currentOrg && !currentOrg.notInterested
      ? allHymnals?.find((hymnal) => hymnal.id === PrimaryOrgHymnalId)
      : undefined;

  const onClickOurHymnal = useCallback(async () => {
    await navigate(getUrlForPage(Pages.Organization) + '/' + orgHymnalSubPage);
  }, [navigate]);

  const ourHymnal = churchHymnal && (
    <HymnalsViewEntry hymnal={churchHymnal} index={0} hymnalCount={1} singleton={true} onClick={onClickOurHymnal} />
  );

  return (
    <HymnsList
      customHeader={customHeader}
      hymnals={allHymnals}
      title={title}
      showProfileAvatar={!isDemoPage}
      weeklySongGroups={hymnals ?? []}
      message={message}
      isDemo={isDemoPage || showDemoSongList}
      isFooterVisibleForDemo={!isDemoPage}
      showThisSundayOnly={showThisSundayOnly}
      onEditHymnal={onEditHymnal}
      firstElement={ourHymnal}
    />
  );
}

const OuterTitleWrapper = styled.div`
  width: 100%;
  /*
     In Safari, making the parent relative to an absolute child
     1) makes the parent larger - AND -
     2) causes the text in the bubble tooltip to wrap while simultaneously displaying ellipsis on the first line
     So, we just let the child (tooltip) be relative to the window in Safari, causing it to center on the entire window
  */
  ${inSafari() ? '' : 'position: relative;'}
`;

// Width is constrained by the Header's title slot, which shrinks to make room for the avatar and icons
const TitleWrapper = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const InnerTitleWrapper = styled.div`
  white-space: nowrap; /* not text-wrap: nowrap, which older iOS Safari lacks */
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronDown = styled(ChevronDownIcon)`
  width: 18px;
  height: 18px;
  margin-left: 10px;
  flex-shrink: 0;
  fill: var(--color-text);
`;

const Tooltip = styled.div`
  width: 14ch;
  font-family: Jost, sans-serif;
  padding: 0.5rem 1rem;
  text-align: center;
`;

const MenuEntryWrapper = styled.div`
  display: flex;
  align-items: center;
`;

function getHymnalsFromSongLists({
  songLists,
  org,
  allHymnals,
  customMusic,
  isDemo,
  showThisSundayOnly,
  isInternalUser,
  isOrgAdmin,
  isStaff,
  mostRecentlyViewedOrgEvents,
  today,
}: {
  songLists: Array<SongList>;
  org?: Org;
  allHymnals: HymnalWithHymns[] | undefined;
  customMusic: CustomMusic;
  isDemo: boolean | undefined;
  showThisSundayOnly: boolean | undefined;
  isInternalUser: boolean;
  isOrgAdmin: boolean;
  isStaff: boolean;
  mostRecentlyViewedOrgEvents: SongList[];
  today: DateString;
}): HymnalWithHymns[] {
  const filteredSongLists = songLists.filter((songList) => !isEmptyLastSundayList(songList, today));

  const filterEvents = (songLists: SongList[]) => {
    const events = songLists.filter((songList) => songList.type === SongListType.Event) as EventSongList[];

    if (!events.length) {
      return [];
    }

    if (isDemo) {
      const date = new Date();
      const futureEvents = events.filter((songList) => dateFromString(songList.date) >= date);
      if (!futureEvents.length) {
        return [];
      }
      let nextEvent = futureEvents[0];
      for (const event of futureEvents) {
        if (event.date < nextEvent.date) {
          nextEvent = event;
        }
      }
      return [nextEvent];
    }

    return events;
  };

  const thisSundayRequest = (
    <div>
      <RequestSongs /> that your music leader upload this week’s songs.
    </div>
  );

  const hymnals = org?.type === OrgType.School ? getHymnalsForSchool() : getHymnalsForOrg(mostRecentlyViewedOrgEvents);

  insertWeeklyPrompt();
  appendEventCalendarPrompt();

  return hymnals;

  function getHymnalsForSchool(): HymnalWithHymns[] {
    // TODO(hewitt): will not be invalidated by React.  Call useLocalStorage and pass it in.
    const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    const todayRange = { start: today, end: today };
    const term = getTermForDateRange(todayRange, schoolTerms);
    let intervalSongLists: Array<IntervalSongList & { name: string }> = [];
    if (term) {
      const sortedOverlappingNonEmptySongLists = filteredSongLists
        .filter(
          (songList) =>
            hasInterval(songList) &&
            dateRangesOverlap(songList.interval, term.interval) &&
            songList.interval.start <= today &&
            songList.songs.filter((song) => isBound(song)).length > 0,
        )
        .sort((lhs, rhs) => dateCompare(getSongListStartDate(rhs), getSongListStartDate(lhs))) as IntervalSongList[];
      const termSongLists = organizeSongListByRowNames(
        sortedOverlappingNonEmptySongLists.find((songList) => songList.interval.type === IntervalType.Custom),
        schoolTerms,
      );
      const monthlySongLists = sortedOverlappingNonEmptySongLists
        .filter(
          (songList) =>
            songList.interval.type === IntervalType.Month && dateRangesOverlap(songList.interval, todayRange),
        )
        .flatMap((songList) => organizeSongListByRowNames(songList, schoolTerms));
      const weeklySongLists = sortedOverlappingNonEmptySongLists
        .filter((songList) => songList.interval.type === IntervalType.Week)
        .flatMap((songList) => organizeSongListByRowNames(songList, schoolTerms));
      intervalSongLists = [...termSongLists, ...monthlySongLists, ...weeklySongLists];
    }
    const events = filterEvents(filteredSongLists).map((event) => ({
      ...event,
      name: getSongListName(event, [], [], today),
    }));
    return [...intervalSongLists, ...events].map((songList) => {
      const hymns = getHymnsFromSongs(songList.songs);
      return {
        id: 0,
        name: songList.name,
        hymns,
        slug: thisWeekHymnalSlug,
        songListType: songList.type,
        message:
          songList.type === SongListType.Event && hymns.length === 0 ? (
            <div>Your music director has not yet added any songs for this event.</div>
          ) : undefined,
      };
    });
  }

  function organizeSongListByRowNames(
    songList: IntervalSongList | undefined,
    schoolTerms: SchoolTerm[],
  ): Array<IntervalSongList & { name: string }> {
    if (!org || !songList) {
      return [];
    }
    const schemaRows: SongListSchemaEntry[] = org.churchLiturgySchema?.rows ?? [];
    const intervalRows = schemaRows.filter((row) => row.intervalType === songList.interval.type);
    const uniqueSchemaRowNames = getUniqueNamesInOrder(intervalRows.map(({ label }) => label));
    return uniqueSchemaRowNames.map((name) => {
      const matchingRows = intervalRows.filter((row) => row.label === name).map(({ row }) => row);
      const matchingSongs = songList.songs.filter((song) => matchingRows.includes(song.row));
      const hymns = getHymnsFromSongs(matchingSongs);
      return { ...songList, name: getSongListName(songList, hymns, schoolTerms, today, name), songs: matchingSongs };
    });
  }

  function getUniqueNamesInOrder(names: (string | undefined)[]): (string | undefined)[] {
    const uniqueNames: (string | undefined)[] = [];
    for (const name of names) {
      if (!uniqueNames.includes(name)) {
        uniqueNames.push(name);
      }
    }
    return uniqueNames;
  }

  function getHymnalsForOrg(mostRecentlyViewedOrgEvents: SongList[]): HymnalWithHymns[] {
    const weeklySongLists = [
      ...filteredSongLists.filter((songList) => songList.type === SongListType.HymnOfTheMonth),
      ...filteredSongLists
        .filter((songList) => songList.type === SongListType.WorshipService)
        .sort((lhs, rhs) => dateCompare(getSongListStartDate(rhs), getSongListStartDate(lhs))),
      ...filterEvents(filteredSongLists),
    ];

    return allHymnals?.length === 0
      ? []
      : weeklySongLists.map((songList) => {
          const hymns = getHymnsFromSongs(songList.songs);
          return {
            id: 0,
            name: getSongListName(songList, hymns, [], today),
            hymns,
            slug: thisWeekHymnalSlug,
            songListType: songList.type,
            isPreview: isDemo,
            message:
              songList.songs.filter((song) => isBound(song)).length !== 0 ? undefined : songList.type ===
                SongListType.WorshipService ? (
                thisSundayRequest
              ) : songList.type === SongListType.Event ? (
                <div>Your music director has not yet added any songs for this event.</div>
              ) : undefined,
            showRedDot:
              songList.type === SongListType.Event &&
              JSON.stringify(songList) !==
                JSON.stringify(mostRecentlyViewedOrgEvents.find(({ id }) => id === songList.id)),
          };
        });
  }

  function getHymnsFromSongs(songs: SongListEntry[]): Hymn[] {
    return songs
      .map((song) => getHymnFromSongListEntry({ song, hymnals: allHymnals, customMusic }))
      .filter((hymn) => hymn) as Hymn[];
  }

  function insertWeeklyPrompt() {
    if (!org?.type) {
      return;
    }

    // prompt goes after hymn of the month
    const containsHymnOfTheMonth = songLists.length > 0 && songLists[0].type === SongListType.HymnOfTheMonth;
    const insertionIndex = containsHymnOfTheMonth ? 1 : 0;

    switch (org.type) {
      case OrgType.SmallGroup:
        return;

      case OrgType.School:
        if (hymnals.find((songList) => songList.songListType !== SongListType.Event)) {
          return;
        }
        if (!isInternalUser && !isOrgAdmin) {
          return;
        }
        hymnals.splice(insertionIndex, 0, {
          id: 0,
          name: 'School Planner',
          hymns: [],
          message: <div />, // prevent hiding empty hymnal
          slug: thisWeekHymnalSlug,
        });
        break;

      case OrgType.Church: {
        if (
          hymnals.find(
            (hymnal) =>
              hymnal.songListType === SongListType.WorshipService &&
              (hymnal.name === thisSundayTitle || hymnal.name === todayTitle),
          )
        ) {
          return;
        }
        hymnals.splice(insertionIndex, 0, {
          id: 0,
          name: getSundayTitle(thisSundayDate(today), today),
          hymns: [],
          message: thisSundayRequest,
          slug: thisWeekHymnalSlug,
        });
        break;
      }

      default:
        ensureUnreachable(org.type);
    }
  }

  function appendEventCalendarPrompt() {
    if (songLists.find((list) => list.type === SongListType.Event)) {
      return;
    }
    if (!isInternalUser && !isOrgAdmin && !isStaff) {
      return;
    }
    if (showThisSundayOnly) {
      return;
    }
    hymnals.push({
      id: 0,
      name: 'Event Calendar',
      hymns: [],
      message: <div />,
      slug: thisWeekHymnalSlug,
      songListType: SongListType.Event,
    });
  }
}

function getSongListName(
  songList: SongList,
  hymns: Hymn[],
  schoolTerms: SchoolTerm[],
  today: DateString,
  name?: string,
): string {
  const { type } = songList;
  const dateStr = getSongListStartDate(songList);
  switch (type) {
    case SongListType.HymnOfTheMonth:
      return getHymnOfTheMonthOrTermTitle(hymns, 'Month', name);
    case SongListType.WorshipService:
      return getSundayTitle(dateStr, today);
    case SongListType.Event: {
      const { name, time } = songList;
      const datePart = dateStr ? ` - ${getDateAsShortString(dateStr)}` : '';
      const shortTime = getShortTime(getTimeFromDateTime(time));
      const timePart = shortTime ? ` @ ${shortTime}` : '';
      return `${name ?? 'Event'}${datePart}${timePart}`;
    }
    case SongListType.Interval: {
      const { interval } = songList;
      switch (interval.type) {
        case IntervalType.Week:
          return getWeeklyListName(songList.interval, schoolTerms, name);
        case IntervalType.Month:
          return getHymnOfTheMonthOrTermTitle(hymns, 'Month', name);
        case IntervalType.Custom:
          return getHymnOfTheMonthOrTermTitle(hymns, 'Term', name);
        case IntervalType.Year:
        case IntervalType.Season:
        case IntervalType.Fixed:
          // TODO(hewitt): combine with other lists
          return 'NYI';
        default:
          ensureUnreachable(interval.type);
      }
      break; // unreachable, but TS complains without it
    }
    case SongListType.Playlist:
      return songList.name ?? defaultPlaylistName;
    case SongListType.Seasonal:
      return seasonalSongListName;
    default:
      ensureUnreachable(type);
  }
}

let requestInProgress = false;

function RequestSongs() {
  const { currentOrg } = useCurrentOrg();
  const [requested, setRequested] = useLocalStorage(LocalStorageKey.SongListRequested);
  const today = useCurrentDate();

  const onClick = useCallback(async () => {
    if (requestInProgress) {
      return;
    }
    requestInProgress = true;
    try {
      if (currentOrg?.token) {
        await server_api.sendEmail({
          type: EmailType.ThisSundaySongListRequest,
          date: thisSundayDate(today),
          churchToken: currentOrg.token,
        });
      }
      setRequested(today);
    } finally {
      requestInProgress = false;
    }
  }, [currentOrg?.token, setRequested, today]);

  return !requested || daysBetween(requested, today) >= 1 ? (
    <RequestButton onClick={onClick}>Request</RequestButton>
  ) : (
    <span>
      <CheckmarkWrapper>
        <CheckMark $size="15px" $padding="" $fillColor="var(--color-checkmark-song)" />
      </CheckmarkWrapper>{' '}
      Requested
    </span>
  );
}

const isEmptyLastSundayList = (songList: SongList, today: DateString) =>
  songList.type === SongListType.WorshipService &&
  songList.date === lastSundayDate(today) &&
  songList.songs.filter((song) => isBound(song)).length === 0;

function OrgMenuEntry({
  org,
  householdOrgs,
  currentOrg,
  setCurrentOrg,
  onToggleOrgChooser,
}: {
  org: Org;
  householdOrgs: Org[];
  currentOrg: Org | undefined;
  setCurrentOrg: (org: OrgBase | undefined) => void;
  onToggleOrgChooser: () => void;
}) {
  const onClick = useCallback(() => {
    setCurrentOrg(org);
    onToggleOrgChooser();
  }, [org, setCurrentOrg, onToggleOrgChooser]);
  return (
    <MenuEntry
      $isFirst={org.id === householdOrgs[0].id}
      $isLast={org.id === householdOrgs[householdOrgs.length - 1].id}
      onClick={onClick}
    >
      <MenuEntryWrapper>
        <CheckMark
          $size="23px"
          $padding="8px"
          $fillColor={org.id === currentOrg?.id ? 'var(--color-text)' : 'transparent'}
        />
        <div>{org.name}</div>
      </MenuEntryWrapper>
    </MenuEntry>
  );
}

const CheckmarkWrapper = styled.div`
  display: inline-block;
`;

const RequestButton = styled.button`
  color: var(--color-text);
  background-color: var(--color-background);
  border-radius: 10px;
  padding: 0 7px;
  border: 2px solid var(--color-text);
  cursor: pointer;
`;
